import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AuthContext, type IAuthContext } from 'react-oauth2-code-pkce';
import { FormTab } from './components/FormTab';
import { formatError } from './utils/api';
import { getApiBaseUrl } from './config/apiConfig';
import { isDevelopment } from './utils/environment';

const FORM_TABS = [
  { name: 'branding', description: 'Branding' },
  { name: 'avatar_backgrounds', description: 'Avatars' },
  { name: 'virtual_backgrounds', description: 'Backgrounds' },
  { name: 'custom_icons', description: 'Custom Icons' },
  { name: 'custom_theme', description: 'Custom Theme' },
  { name: 'custom_palette', description: 'Custom Palette' },
  { name: 'labels', description: 'Labels (en)' },
];

const App: React.FC = () => {
  const authContext = useContext<IAuthContext>(AuthContext);
  const isAuthenticated = Boolean(authContext.idToken);
  const idToken = authContext.idToken || undefined;
  const [activeTab, setActiveTab] = useState(FORM_TABS[0]?.name ?? 'branding');
  const [saveSignal, setSaveSignal] = useState(0);
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const [validationMap, setValidationMap] = useState<Record<string, boolean>>({});
  const [errorMap, setErrorMap] = useState<Record<string, string | null>>({});
  const [pendingMap, setPendingMap] = useState<Record<string, string[]>>({});
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});

  const handleDirtyChange = useCallback((name: string, isDirty: boolean) => {
    setDirtyMap((prev) => ({ ...prev, [name]: isDirty }));
  }, []);

  const handleValidationChange = useCallback((name: string, hasErrors: boolean) => {
    setValidationMap((prev) => ({ ...prev, [name]: hasErrors }));
  }, []);

  const handleErrorChange = useCallback((name: string, message: string | null) => {
    setErrorMap((prev) => ({ ...prev, [name]: message }));
  }, []);

  const handlePendingChange = useCallback((name: string, paths: string[]) => {
    setPendingMap((prev) => ({ ...prev, [name]: paths }));
  }, []);

  const handleSavingChange = useCallback((name: string, isSaving: boolean) => {
    setSavingMap((prev) => ({ ...prev, [name]: isSaving }));
  }, []);

  const errorMessage = useMemo(() => {
    const errors = [authContext.error, ...Object.values(errorMap)].filter(Boolean);
    if (errors.length === 0) {
      return null;
    }
    return errors.map(formatError).join(' | ');
  }, [authContext.error, errorMap]);

  const hasDirtyForms = Object.values(dirtyMap).some(Boolean);
  const hasValidationErrors = FORM_TABS.some(
    (tab) => dirtyMap[tab.name] && validationMap[tab.name],
  );
  const pendingCount = Object.values(pendingMap).reduce((sum, paths) => sum + paths.length, 0);
  const savingCount = Object.values(savingMap).filter(Boolean).length;

  if (!isAuthenticated) {
    return (
      <div className="app">
        {errorMessage && (
          <div className="error-banner">
            <span>{errorMessage}</span>
          </div>
        )}
        <div className="auth-gate">
          <div className="auth-card">
            <h1>Please authenticate to use this service</h1>
            <p>Access is restricted to authorized users.</p>
            <button
              className="primary-button"
              type="button"
              onClick={() => authContext.logIn()}
              disabled={authContext.loginInProgress}
            >
              {authContext.loginInProgress ? 'Authenticating...' : 'Authenticate'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const baseApiUrl = getApiBaseUrl(isDevelopment());

  return (
    <div className="app">
      {errorMessage && (
        <div className="error-banner">
          <span>{errorMessage}</span>
        </div>
      )}
      <div className="content">
        <div className="content-inner">
          <div className="content-header">
            <h2>Branding Configuration ({baseApiUrl})</h2>
            <div className="header-actions">
              <div className="save-actions">
                {hasDirtyForms &&
                    <span className="save-status">Unsaved data</span>
                }
                {pendingCount > 0 && <span className="save-status">{pendingCount} pending</span>}
                {savingCount > 0 && <span className="save-status saving">Saving...</span>}
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setSaveSignal((prev) => prev + 1)}
                  disabled={!hasDirtyForms || hasValidationErrors}
                >
                  Save
                </button>
              </div>
              <button className="secondary-button" type="button" onClick={() => authContext.logOut()}>
                Log out
              </button>
            </div>
          </div>
          <div className="tabs">
            {FORM_TABS.map((tab) => (
              <button
                key={tab.name}
                className={`tab-button${activeTab === tab.name ? ' active' : ''}`}
                type="button"
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.description}
                {savingMap[tab.name] && <span className="tab-saving-dot" aria-hidden="true" />}
              </button>
            ))}
          </div>
          {FORM_TABS.map((tab) => (
            <FormTab
              key={tab.name}
              name={tab.name}
              description={tab.description}
              idToken={idToken}
              isActive={activeTab === tab.name}
              saveSignal={saveSignal}
              onDirtyChange={handleDirtyChange}
              onValidationChange={handleValidationChange}
              onErrorChange={handleErrorChange}
              onPendingChange={handlePendingChange}
              onSavingChange={handleSavingChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default App;

